import { useMemo, useState } from "react";
import styled from "@xstyled/styled-components";

import { LocationCheckbox, LocationCheckboxList } from "./LocationCheckbox";

import { Overline, Spacing, Text } from "@otta/design";
import { Loading } from "@otta/search/components/Loading";
import { Location as LocationType } from "@otta/search/schema";
import { useUserLocation } from "@otta/search/hooks/useUserLocation";
import {
  LOCATION_NAME,
  sortByProximity,
  sortByLondonFirst,
} from "@otta/search/utils/locations";
import {
  Country,
  DisplayRegion,
  LabelledLocation,
  isEUCountry,
  isNorthAmericaCountry,
  locationCountries,
} from "@otta/search/constants/locations";
import { partition } from "@otta/search/utils/collections";

/**
 * A group of locations that should be shown together, so if the user's location
 * is part of the group then all the other locations within the group will be shown
 * above the "show more" line.
 */
export type LocationGroup = {
  locations: ReadonlyArray<LocationType>;
  visible?(country?: Country): boolean;
};

type LocationsByProximity = {
  international: readonly LabelledLocation[];
  nearby: readonly LabelledLocation[];
};

type LocationsByGeography = {
  nearby: readonly LabelledLocation[];
  international: {
    name: DisplayRegion;
    locations: readonly LabelledLocation[];
  }[];
};

type Props = {
  value: readonly LocationType[];
  options: readonly LocationGroup[];
  valueChanged(locs: LocationType[]): void;
  remoteChanged?(checked: boolean): void;
  remoteSelected?: boolean;
  showRemote?: boolean;
  loading: boolean;
  useGeographicalGrouping?: boolean;
};

const ClickableText = styled(Text)`
  text-decoration: underline;
  &:hover {
    cursor: pointer;
  }
`;

function mapToLabel(locations: LocationType[]): readonly LabelledLocation[] {
  return locations.flatMap(location => {
    const label = LOCATION_NAME[location];
    return label ? { location, label } : [];
  });
}

/**
 * Given a country we think the user is located in and grouped locations
 * then place the group(s) containing that country into nearby and others into international
 */
function groupByProximity(
  country: Country | undefined,
  options: readonly LocationGroup[]
): LocationsByProximity {
  const forCountry = options.filter(o => !o.visible || o.visible(country));
  if (country === undefined) {
    return {
      nearby: mapToLabel(forCountry.flatMap(l => l.locations)),
      international: [],
    };
  }
  const [international, nearby] = partition(forCountry)(({ locations }) =>
    locations.some(l => !country || locationCountries[l] === country)
  );

  const [others, top] = partition(nearby.flatMap(n => n.locations))(
    t => country !== undefined && locationCountries[t] === country
  );

  return {
    international: mapToLabel(international.flatMap(i => i.locations)),
    nearby: mapToLabel([...top, ...others]),
  };
}

/**
 * Group locations by geographical regions for the experiment, with user's region first
 */
function groupByGeography(
  country: Country | undefined,
  options: readonly LocationGroup[]
): LocationsByGeography {
  const forCountry = options.filter(o => !o.visible || o.visible(country));
  const allLocations = forCountry.flatMap(l => l.locations);
  const ukVisibleLocations: LocationType[] = allLocations.filter(
    location => locationCountries[location] === Country.UK
  );
  const euVisibleLocations: LocationType[] = allLocations.filter(location =>
    isEUCountry(locationCountries[location])
  );
  const northAmericaVisibleLocations: LocationType[] = allLocations.filter(
    location => isNorthAmericaCountry(locationCountries[location])
  );

  const ukGrouped = {
    name: "United Kingdom" as DisplayRegion,
    // All locations in same country for UK - no sorting needed
    locations: mapToLabel(sortByLondonFirst(ukVisibleLocations)),
  };
  const euGrouped = {
    name: "Europe" as DisplayRegion,
    locations: mapToLabel(sortByProximity(euVisibleLocations, country)),
  };
  const northAmericaGrouped = {
    name: "North America" as DisplayRegion,
    locations: mapToLabel(
      sortByProximity(northAmericaVisibleLocations, country)
    ),
  };

  const defaultReturn = {
    nearby: ukGrouped.locations,
    international: [euGrouped, northAmericaGrouped],
  };

  if (country === Country.UK) {
    return defaultReturn;
  } else if (isNorthAmericaCountry(country)) {
    return {
      nearby: northAmericaGrouped.locations,
      international: [ukGrouped, euGrouped],
    };
  } else if (isEUCountry(country)) {
    return {
      nearby: euGrouped.locations,
      international: [ukGrouped, northAmericaGrouped],
    };
  } else {
    return defaultReturn;
  }
}

/**
 * Do our selected preferences include
 * international locations?
 */
function shouldShowMore(
  prefs: readonly LocationType[],
  opts: LocationsByProximity
) {
  const oneEmpty = opts.international.length === 0 || opts.nearby.length === 0;
  const hasIntl = opts.international.some(l => prefs.includes(l.location));
  return hasIntl || oneEmpty;
}

/**
 * The purpose of this component is to handle showing groups of locations,
 * choosing the best group to show according to where you are and hiding the rest,
 * unless you've previously chosen locations in multiple groups.
 */
export function CommonLocation(props: Props): React.ReactElement {
  const { country, loading: loadingCountry } = useUserLocation();

  const useGeoGrouping = props.useGeographicalGrouping !== false;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialCountry = useMemo(() => country, [country !== null]);
  const [clickedShowMore, setShowMore] = useState(false);
  const [expandedRegions, setExpandedRegions] = useState<Set<DisplayRegion>>(
    new Set()
  );

  const options = useMemo(
    () =>
      useGeoGrouping
        ? groupByGeography(initialCountry, props.options)
        : groupByProximity(initialCountry, props.options),
    [props.options, initialCountry, useGeoGrouping]
  );

  const showInternational = useMemo(() => {
    if (useGeoGrouping) {
      return false;
    } else {
      return (
        clickedShowMore ||
        shouldShowMore(props.value, options as LocationsByProximity)
      );
    }
  }, [options, props.value, clickedShowMore, useGeoGrouping]);

  if (showInternational && !clickedShowMore) {
    setShowMore(true);
  }

  if (useGeoGrouping) {
    // Set up expanded regions for new locations UI
    const locationsByGeography = options as LocationsByGeography;
    locationsByGeography.international.forEach(region => {
      const isSelectedRegion = region.locations.some(l =>
        props.value.includes(l.location)
      );

      if (isSelectedRegion && !expandedRegions.has(region.name)) {
        setExpandedRegions(expandedRegions.add(region.name));
      }
    });
  }

  if (props.loading || loadingCountry) {
    return <Loading />;
  }

  const handleChange = (preference: string, checked: boolean) => {
    const set = new Set(props.value);
    const loc: LocationType = preference as LocationType;
    checked ? set.add(loc) : set.delete(loc);
    props.valueChanged(Array.from(set));
  };

  if (useGeoGrouping) {
    const geoOptions = options as LocationsByGeography;
    return (
      <div>
        <Spacing size={5}>
          <Spacing size={-4}>
            {props.showRemote && (
              <LocationCheckbox
                label={"Remote"}
                onChange={(_, checked) => {
                  props.remoteChanged && props.remoteChanged(checked);
                }}
                checked={props.remoteSelected ?? false}
                value={"remoteOpportunities"}
              />
            )}
            <LocationCheckboxList
              locations={geoOptions.nearby}
              value={props.value}
              handleChange={handleChange}
            />
          </Spacing>
          {/* International regions - expandable */}
          {geoOptions.international.map(region => (
            <div key={region.name}>
              {!expandedRegions.has(region.name) && (
                <ClickableText
                  onClick={() =>
                    setExpandedRegions(prev => new Set([...prev, region.name]))
                  }
                  bold
                >
                  {region.name}
                </ClickableText>
              )}
              {expandedRegions.has(region.name) && (
                <Spacing>
                  <Overline size={-1}>{region.name}</Overline>
                  <LocationCheckboxList
                    locations={region.locations}
                    value={props.value}
                    handleChange={handleChange}
                  />
                </Spacing>
              )}
            </div>
          ))}
        </Spacing>
      </div>
    );
  } else {
    const proximityOptions = options as LocationsByProximity;
    return (
      <div>
        <Spacing size={5}>
          <Spacing size={-4}>
            {proximityOptions.nearby.map(({ location, label }) => (
              <LocationCheckbox
                key={location}
                label={label}
                onChange={handleChange}
                checked={props.value.some(pref => pref === location)}
                value={location}
              />
            ))}
            {props.showRemote && (
              <LocationCheckbox
                label={"Remote"}
                onChange={(preference, checked) => {
                  props.remoteChanged && props.remoteChanged(checked);
                }}
                checked={props.remoteSelected ?? false}
                value={"remoteOpportunities"}
              />
            )}
          </Spacing>
          {!showInternational && (
            <ClickableText
              onClick={() => setShowMore(true)}
              data-testid="show-international-countries-btn"
              bold
            >
              View more locations
            </ClickableText>
          )}
          {showInternational && proximityOptions.international.length > 0 && (
            <Spacing>
              <Overline size={-1}>more locations</Overline>
              <Spacing size={-4}>
                {proximityOptions.international.map(({ location, label }) => (
                  <LocationCheckbox
                    key={location}
                    label={label}
                    onChange={handleChange}
                    checked={props.value.some(pref => pref === location)}
                    value={location}
                  />
                ))}
              </Spacing>
            </Spacing>
          )}
        </Spacing>
      </div>
    );
  }
}
